import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import briefIcon from '../../asset/images/icons/doc.svg';
import cashIcon from '../../asset/images/icons/cash.svg';
import growthIcon from '../../asset/images/icons/growth.svg';
import { InfluencerOnboardingProgress } from './InfluencerOnboadingProgress';
import { JoinBuzzBridgeProgramButton } from './JoinBuzzBridgeProgramButton';
import { GoHomeButton } from '../GoHomeButton/GoHomeButton';

interface InfluencerPartnershipContainerProps {
    onClose?: () => void;
    isLandingPage?: boolean;
}

const lightModeGradients = [
    ['#a18cd1', '#fbc2eb'],
    ['#fbc2eb', '#a6c1ee'],
    ['#fceabb', '#f8b500'],
];

const darkModeGradients = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
];

const benefits = [
    {
        title: 'Campaign Briefs',
        description: 'Exclusive access to premium brand campaigns',
        gradient: ['#667eea', '#764ba2'],
        image: briefIcon,
    },
    {
        title: 'Guaranteed Pay',
        description: 'Competitive rates for every completed project',
        gradient: ['#f093fb', '#f5576c'],
        image: cashIcon,
    },
    {
        title: 'Portfolio Growth',
        description: 'Build your brand with premium collaborations',
        gradient: ['#4facfe', '#00f2fe'],
        image: growthIcon,
    },
];

const GradientIcon = ({ image, id }: { id: string; image: string }) => (
    <img src={image} style={{ height: '50px', width: '50px' }} key={id} alt={id} />
);

export const InfluencerPartnershipContainer: React.FC<InfluencerPartnershipContainerProps> = ({
    onClose,
    isLandingPage,
}) => {
    const { darkModeEnabled: isDark } = useThemeContext();
    const backgroundColor = isDark ? '#0a0e1a' : '#f5f7fa';
    const heroGradient = isDark
        ? 'linear-gradient(90deg, #181C24 0%, #232B3E 100%)'
        : 'linear-gradient(90deg, #ffffff 0%, #e3eaf2 100%)';
    const textColor = isDark ? '#fff' : '#1e293b';
    const subTextColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
    const gradients = isDark ? darkModeGradients : lightModeGradients;

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                background: backgroundColor,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                pb: 4,
            }}>
            {/* Hero Section */}
            <Box
                sx={{
                    width: '100vw',
                    position: 'relative',
                    height: '60vh',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: heroGradient,
                    textAlign: 'center',
                    py: { xs: 12, md: 12 },
                }}>
                {onClose && !isLandingPage && (
                    <CloseIcon
                        onClick={onClose}
                        stroke={textColor}
                        style={{
                            position: 'absolute',
                            top: '5%',
                            right: '5%',
                            height: '50px',
                            width: '50px',
                            cursor: 'pointer',
                        }}
                    />
                )}
                {isLandingPage && (
                    <GoHomeButton
                        text="Go Home"
                        sx={{
                            position: 'absolute',
                            top: '5%',
                            right: '5%',
                            cursor: 'pointer',
                        }}
                    />
                )}
                <Typography
                    variant="h2"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 20, sm: 40, md: 64 },
                        fontWeight: 800,
                        background: isDark
                            ? 'linear-gradient(135deg, #667eea 0%,rgb(162, 75, 75) 100%)'
                            : 'linear-gradient(135deg, #7b4397 0%, #dc2430 100%)',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: isDark ? 'none' : '0 1px 1px rgba(0,0,0,0.1)', // optional
                    }}>
                    BuzzBridge+
                </Typography>
                <Typography
                    sx={{
                        maxWidth: {
                            xs: '60%',
                            md: 600,
                        },
                        mx: 'auto',
                        mt: 2,
                        color: subTextColor,

                        fontSize: {
                            xs: 12,
                            md: 24,
                        },
                    }}>
                    Earn as an Influencer. Join our exclusive program and get paid for premium brand collaborations
                </Typography>
                <JoinBuzzBridgeProgramButton gradients={gradients} />

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        width: '100%',
                        height: 100,
                        overflow: 'hidden',
                        lineHeight: 0,
                    }}>
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" width="100%" height="120">
                        <polygon
                            points="0,0 1440,40 1440,80 0,80"
                            fill={isDark ? '#0a0e1a' : '#f5f7fa'} // ← light background color
                        />
                    </svg>
                </Box>
            </Box>

            {/** Steps */}
            <InfluencerOnboardingProgress readOnly />

            {/* Feathered Waves SVG Fixed Background */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 2,
                    pointerEvents: 'none',
                    opacity: 0.1,
                    overflow: 'hidden',
                }}>
                <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 1920 1080"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    style={{ display: 'block' }}>
                    <path d="M0 700 Q 480 800 960 700 T 1920 700 V1080 H0Z" fill="url(#wave1)" opacity="0.5">
                        <animate
                            attributeName="d"
                            dur="8s"
                            repeatCount="indefinite"
                            values="M0 700 Q 480 800 960 700 T 1920 700 V1080 H0Z;
                                    M0 720 Q 480 680 960 720 T 1920 700 V1080 H0Z;
                                    M0 700 Q 480 800 960 700 T 1920 700 V1080 H0Z"
                        />
                    </path>
                    <path d="M0 800 Q 640 900 1280 800 T 1920 800 V1080 H0Z" fill="url(#wave2)" opacity="0.4">
                        <animate
                            attributeName="d"
                            dur="10s"
                            repeatCount="indefinite"
                            values="M0 800 Q 640 900 1280 800 T 1920 800 V1080 H0Z;
                                    M0 820 Q 640 780 1280 820 T 1920 800 V1080 H0Z;
                                    M0 800 Q 640 900 1280 800 T 1920 800 V1080 H0Z"
                        />
                    </path>
                    <path d="M0 900 Q 960 950 1920 900 V1080 H0Z" fill="url(#wave3)" opacity="0.3">
                        <animate
                            attributeName="d"
                            dur="12s"
                            repeatCount="indefinite"
                            values="M0 900 Q 960 950 1920 900 V1080 H0Z;
                                    M0 940 Q 960 900 1920 940 V1080 H0Z;
                                    M0 900 Q 960 950 1920 900 V1080 H0Z"
                        />
                    </path>
                    <defs>
                        <linearGradient id="wave1" x1="0" y1="0" x2="1920" y2="1080" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#42a5f5" />
                            <stop offset="1" stopColor="#ab47bc" />
                        </linearGradient>
                        <linearGradient id="wave2" x1="0" y1="0" x2="1920" y2="1080" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#26c6da" />
                            <stop offset="1" stopColor="#42a5f5" />
                        </linearGradient>
                        <linearGradient id="wave3" x1="0" y1="0" x2="1920" y2="1080" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#ab47bc" />
                            <stop offset="1" stopColor="#26c6da" />
                        </linearGradient>
                    </defs>
                </svg>
            </Box>

            {/* Benefits with Modern Feathered Waves Background */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    mt: 10,
                    px: 2,
                    zIndex: 2,
                }}>
                <Box
                    sx={{
                        maxWidth: 1200,
                        mx: 'auto',
                        textAlign: 'center',
                    }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 24, md: 24 },
                            fontWeight: 700,
                            color: textColor,
                            mb: 4,
                        }}>
                        Benefits
                    </Typography>

                    <Grid container spacing={4} justifyContent="center">
                        {benefits.map(({ title, description, image }, idx) => (
                            <Grid item xs={12} md={4} key={idx}>
                                <Box
                                    sx={{
                                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                                        p: 4,
                                        borderRadius: 3,
                                        textAlign: 'center',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        color: textColor,
                                        boxShadow: isDark
                                            ? '0 2px 12px rgba(0,0,0,0.10)'
                                            : '0 2px 12px rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                                            transform: 'translateY(-4px)',
                                            transition: 'all 0.3s ease',
                                        },
                                    }}>
                                    <GradientIcon id={`gradient-${idx}`} image={image} />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 2,
                                            fontWeight: 700,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: textColor,
                                        }}>
                                        {title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1,
                                            color: subTextColor,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {description}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* How It Works Section */}
                    <Box
                        sx={{
                            mt: 10,
                            maxWidth: 800,
                            mx: 'auto',
                            px: 3,
                            textAlign: 'left',
                        }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 20, md: 28 },
                                color: textColor,
                                mb: 2,
                                textAlign: 'center',
                            }}>
                            How it works
                        </Typography>
                        <ol
                            style={{
                                color: subTextColor,
                                fontSize: '1rem',
                                lineHeight: 1.8,
                                paddingLeft: '1.5rem',
                            }}>
                            <li>Click "Join Program" to submit your interest as an influencer.</li>
                            <li>Get verified and matched with relevant premium campaigns.</li>
                            <li>Collaborate with top brands and create great content.</li>
                            <li>Receive guaranteed payments for each completed campaign.</li>
                        </ol>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
